import { Dialog, DialogContent, Tab } from "@mui/material";
import { MprcDialogTitleClose, MprcRequestErrorReport, MprcSpinnerIndicator } from "components";
import { MprcFilterAppBar } from "components/filter";
import { MprcBarTabs } from "components/MprcBarTabs";
import { MprcPrompt } from "components/MprcPrompt";
import { ManagedRoleUserConflitResolutionResponse, RolePermissionConstraintsDto } from "generated/CoreApi_v1";
import { useCallbackPrompt } from "hooks/useCallbackPrompt";
import LibUtils from "lib/LibUtils";
import { useCoreApi } from "providers/CoreApiProvider";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { EditRoleUserConflits } from "./EditRoleUserConflits";
import { EditRoleUserConstraints } from "./EditRoleUserConstraints";

////////////////////////////////////////////////////////////////////////////

type TabOptions = "constraints" | "conflitResolution";

////////////////////////////////////////////////////////////////////////////

interface IProps {
    roleId: string;
    roleName: string;
    isReserved: boolean;
    userId: string;
    userName: string;
    onClose: (reload: boolean) => void;
}

////////////////////////////////////////////////////////////////////////////

/**
 * Edição das Permissões de um Papel Segurança
 * @param props
 */
export const EditRoleUserTabs: React.FC<IProps> = (props) => {

    ////////////////////////////////////////////////////////////////////////////
    const { roleId, roleName, isReserved, userId, userName, onClose } = props;

    const [currentTab, setTab] = useState<TabOptions>("constraints");
    const [loading, setLoading] = useState<boolean>(true);
    const [refreshOnClose, setRefreshOnClose] = useState<boolean>(false);
    const [viewError, setViewError] = useState<any>();
    const [userConstraints, setUserConstraints] = useState<RolePermissionConstraintsDto[]>();
    const [userConflit, setUserConflit] = useState<ManagedRoleUserConflitResolutionResponse>();
    const [originalRecord, setOriginalRecord] = useState<RolePermissionConstraintsDto[]>();
    const [hasOwnConstraints, setHasOwnConstraints] = useState<boolean>(false);

    const api = useCoreApi();
    const { t } = useTranslation();

    const recordChanged = currentTab == "constraints"
        ? !LibUtils.deepCompare(originalRecord, userConstraints)
        : !LibUtils.deepCompare(originalRecord, userConflit);

    const [showPrompt, confirmNavigation, cancelNavigation] = useCallbackPrompt(recordChanged);

    ////////////////////////////////////////////////////////////////////////////
    /// Effect's
    ////////////////////////////////////////////////////////////////////////////

    // Ao carregar...
    useEffect(() => {
        ///
        async function getData() {
            try {
                if (currentTab == "constraints") {

                    let result = await api.v1.securityRoleManagement.getUserConstraints({ roleId, userId });
                    if (result.data != null) {
                        setHasOwnConstraints(result.data.hasOwnConstraints);
                        setUserConstraints(result.data.permissions);
                        setOriginalRecord(LibUtils.deepCopy(result.data.permissions));
                    }
                }
                else {
                    let result = await api.v1.securityRoleManagement.getUserConflitResolution({ roleId, userId });
                    if (result.data != null) {
                        setUserConflit(result.data);
                        setOriginalRecord(LibUtils.deepCopy(result.data));
                    }
                }
            }
            catch (e: any) {
                setViewError(e as Error);
            }
            setLoading(false);
        }

        if (loading) getData();
    }, [loading]);

    ////////////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open
            fullWidth
            maxWidth="md"
        >
            <MprcDialogTitleClose
                title={t("Role.Edit.User.Tabs.UserConstraintsTitle")}
                subtitle={t("Role.Edit.User.Tabs.UserConstraintsSubtitle", { userName, roleName: roleName ?? "?" })}
                onClose={recordChanged ? undefined : () => onClose(refreshOnClose)}
            >
                <MprcFilterAppBar margin="none">
                    <MprcBarTabs
                        variant="fullWidth"
                        value={currentTab}
                        onChange={(v, selected) => {
                            if (!recordChanged) {
                                setTab(selected);
                                setLoading(true);
                            }
                        }}
                    >
                        <Tab value="constraints" label={isReserved ? t("Role.Edit.User.Tabs.ViewConstraintsLabel") : t("Role.Edit.User.Tabs.CustomizeConstraintsLabel")} />
                        {
                            !isReserved && hasOwnConstraints &&
                            <Tab value="conflitResolution" label={t("Role.Edit.User.Tabs.ConflictResolutionLabel")} />
                        }
                    </MprcBarTabs>
                </MprcFilterAppBar>
            </MprcDialogTitleClose>

            {
                viewError
                    ? (
                        <DialogContent sx={{ pt: 0 }}>
                            <MprcRequestErrorReport error={viewError} />
                        </DialogContent>
                    )
                    : loading
                        ? (
                            <DialogContent sx={{ pt: 0, height: "100vh" }}>
                                <MprcSpinnerIndicator />
                            </DialogContent>
                        )
                        : currentTab == "constraints"
                            ? <EditRoleUserConstraints
                                userId={userId}
                                roleId={roleId}
                                isReserved={isReserved}
                                recordChanged={recordChanged}
                                permissions={userConstraints!}
                                onReload={
                                    () => {
                                        setLoading(true);
                                        setRefreshOnClose(true);
                                    }
                                }
                                onFormUpdate={
                                    (permissions: RolePermissionConstraintsDto[]) => setUserConstraints(permissions)
                                }
                            />
                            : <EditRoleUserConflits
                                userId={userId}
                                roleId={roleId}
                                recordChanged={recordChanged}
                                viewData={userConflit!}
                                onReload={
                                    () => {
                                        setLoading(true);
                                    }
                                }
                                onFormUpdate={
                                    (record: ManagedRoleUserConflitResolutionResponse) => setUserConflit(record)
                                }
                            />
            }

            <MprcPrompt
                open={showPrompt}
                onConfirm={confirmNavigation}
                onCancel={cancelNavigation} />

        </Dialog >
    );
};
